/**
 * IconLibrary — catálogo curado de íconos profesionales para el editor.
 *
 * Reemplaza el set viejo de 23 SVGs hardcoded de FitnessIcons.tsx con un
 * pull selectivo de lucide-react (1500+ íconos open-source MIT, tree-shaken
 * por Vite — solo se bundlean los que importamos acá).
 *
 * Selección: 110 íconos relevantes para el dominio coaching/fitness/nutrición/
 * UI/medidas/números, agrupados en categorías. Tree-shaking de lucide-react
 * implica que el bundle solo incluye lo que se importa por nombre — no los
 * 1500.
 *
 * Para agregar más íconos: agregar el import + entry en CATALOG. El picker
 * los renderiza automáticamente desde CATALOG.
 *
 * Compatibilidad con SVG legacy (sesiones 1-6): los planes pueden tener
 * íconos guardados como `FitnessIconName` (sentadilla, mancuerna, etc.).
 * Para esos seguimos usando `FitnessIcons.tsx` — este módulo es ADICIONAL,
 * no reemplazo. El picker nuevo expone ambos sets.
 */
import * as React from "react";
import {
  // === Fitness / Ejercicio ===
  Dumbbell,
  Activity,
  Bike,
  Footprints,
  PersonStanding,
  HeartPulse,
  Zap,
  Flame,
  Wind,
  Mountain,
  // === Anatomía / Cuerpo ===
  Brain,
  Heart,
  Hand,
  Eye,
  // === Tiempo / Reps ===
  Timer,
  Clock,
  Hourglass,
  Calendar,
  CalendarDays,
  CalendarCheck,
  Repeat,
  // === Nutrición / Comida ===
  Apple,
  Beef,
  Cherry,
  Coffee,
  Cookie,
  Drumstick,
  Egg,
  Fish,
  IceCreamCone,
  Pizza,
  Salad,
  Sandwich,
  Soup,
  Wheat,
  GlassWater,
  Milk,
  // === Métricas / Medidas ===
  Scale,
  Ruler,
  Gauge,
  TrendingUp,
  TrendingDown,
  ChartBar,
  ChartLine,
  Percent,
  Hash,
  // === Logros / Motivación ===
  Trophy,
  Medal,
  Award,
  Star,
  Crown,
  Target,
  Sparkles,
  // === Tiempo del día / Naturaleza ===
  Sun,
  Sunrise,
  Sunset,
  Moon,
  Snowflake,
  CloudSun,
  Leaf,
  Sprout,
  TreePine,
  Waves,
  // === Bienestar / Mente ===
  Smile,
  Meh,
  ThumbsUp,
  Music,
  Headphones,
  // === Acciones / UI ===
  Check,
  CheckCheck,
  X,
  Plus,
  Minus,
  ChevronRight,
  ChevronLeft,
  ChevronUp,
  ChevronDown,
  ArrowUp,
  ArrowDown,
  ArrowRight,
  Play,
  Pause,
  CirclePlay,
  // === Números y orden ===
  CircleDot,
  Circle,
  Square,
  Triangle,
  Hexagon,
  // === Salud / Médico ===
  Stethoscope,
  Pill,
  Bandage,
  Syringe,
  Thermometer,
  // === Equipamiento gym ===
  Anchor,
  Cable,
  Link as LinkIcon,
  // === Misceláneos útiles ===
  Bookmark,
  BookOpen,
  Camera,
  Compass,
  Flag,
  Lightbulb,
  MapPin,
  Quote,
  Rocket,
  Shield,
  Sword,
  Tag,
  type LucideIcon,
} from "lucide-react";
import { getPhosphorEntry } from "./PhosphorIcons";

/** Categorías ordenadas para el picker. */
export type IconCategory =
  | "Ejercicio"
  | "Anatomía"
  | "Tiempo"
  | "Nutrición"
  | "Métricas"
  | "Logros"
  | "Naturaleza"
  | "Bienestar"
  | "UI / Acciones"
  | "Formas"
  | "Salud"
  | "Equipo gym"
  | "Otros";

export interface IconEntry {
  /** id estable que se persiste en plan.sections[].data.decorations[].icon */
  id: string;
  /** Componente Lucide a renderizar */
  Component: LucideIcon;
  /** Label legible para tooltip */
  label: string;
  /** Categoría para agrupar en el picker */
  category: IconCategory;
  /** Tags para búsqueda fuzzy (en español) */
  tags: string[];
}

/**
 * Catálogo curado. Para agregar uno nuevo:
 *   1. Importar el componente de lucide-react arriba
 *   2. Agregar la entrada acá con id (snake-case), label, categoría y tags
 *   3. El picker lo muestra automático
 */
export const ICON_CATALOG: IconEntry[] = [
  // === Ejercicio ===
  { id: "dumbbell",       Component: Dumbbell,       label: "Mancuerna",        category: "Ejercicio", tags: ["mancuerna","pesa","gym","peso"] },
  { id: "activity",       Component: Activity,       label: "Actividad",        category: "Ejercicio", tags: ["pulso","cardio","actividad"] },
  { id: "bike",           Component: Bike,           label: "Bicicleta",        category: "Ejercicio", tags: ["bici","bicicleta","spinning","cardio"] },
  { id: "footprints",     Component: Footprints,     label: "Caminar",          category: "Ejercicio", tags: ["pasos","caminar","correr","footprint"] },
  { id: "person-standing",Component: PersonStanding, label: "Persona de pie",   category: "Ejercicio", tags: ["persona","postura","de pie"] },
  { id: "heart-pulse",    Component: HeartPulse,     label: "Frecuencia cardíaca",category: "Ejercicio", tags: ["pulso","ritmo","corazón","ekg"] },
  { id: "zap",            Component: Zap,            label: "Energía",          category: "Ejercicio", tags: ["energía","explosivo","rayo","potencia"] },
  { id: "flame",          Component: Flame,          label: "Quemar",           category: "Ejercicio", tags: ["fuego","quemar","calorías","intenso"] },
  { id: "wind",           Component: Wind,           label: "Respiración",      category: "Ejercicio", tags: ["aire","respiración","cardio","viento"] },
  { id: "mountain",       Component: Mountain,       label: "Montaña",          category: "Ejercicio", tags: ["montaña","desafío","escalada"] },

  // === Anatomía ===
  { id: "brain",          Component: Brain,          label: "Cerebro",          category: "Anatomía",  tags: ["cerebro","mente","mental"] },
  { id: "heart",          Component: Heart,          label: "Corazón",          category: "Anatomía",  tags: ["corazón","amor","cardio"] },
  { id: "hand",           Component: Hand,           label: "Mano",             category: "Anatomía",  tags: ["mano","agarre","grip"] },
  { id: "eye",            Component: Eye,            label: "Ojo",              category: "Anatomía",  tags: ["ojo","vista","visión","focus"] },

  // === Tiempo ===
  { id: "timer",          Component: Timer,          label: "Cronómetro",       category: "Tiempo",    tags: ["timer","cronómetro","tiempo"] },
  { id: "clock",          Component: Clock,          label: "Reloj",            category: "Tiempo",    tags: ["reloj","hora","tiempo"] },
  { id: "hourglass",      Component: Hourglass,      label: "Reloj de arena",   category: "Tiempo",    tags: ["arena","reloj","tiempo"] },
  { id: "calendar",       Component: Calendar,       label: "Calendario",       category: "Tiempo",    tags: ["fecha","calendario","día"] },
  { id: "calendar-days",  Component: CalendarDays,   label: "Días calendario",  category: "Tiempo",    tags: ["calendario","días","semana","mes"] },
  { id: "calendar-check", Component: CalendarCheck,  label: "Día completado",   category: "Tiempo",    tags: ["completado","checkin","fecha"] },
  { id: "repeat",         Component: Repeat,         label: "Repetir",          category: "Tiempo",    tags: ["repetir","ciclo","reps","series"] },

  // === Nutrición ===
  { id: "apple",          Component: Apple,          label: "Manzana",          category: "Nutrición", tags: ["manzana","fruta","saludable"] },
  { id: "beef",           Component: Beef,           label: "Carne",            category: "Nutrición", tags: ["carne","proteína","res"] },
  { id: "cherry",         Component: Cherry,         label: "Cereza",           category: "Nutrición", tags: ["fruta","cereza","antioxidante"] },
  { id: "coffee",         Component: Coffee,         label: "Café",             category: "Nutrición", tags: ["café","cafeína","pre-entreno"] },
  { id: "cookie",         Component: Cookie,         label: "Galleta",          category: "Nutrición", tags: ["galleta","dulce","snack"] },
  { id: "drumstick",      Component: Drumstick,      label: "Pollo",            category: "Nutrición", tags: ["pollo","proteína","ave"] },
  { id: "egg",            Component: Egg,            label: "Huevo",            category: "Nutrición", tags: ["huevo","proteína","desayuno"] },
  { id: "fish",           Component: Fish,           label: "Pescado",          category: "Nutrición", tags: ["pescado","omega","proteína"] },
  { id: "ice-cream-cone", Component: IceCreamCone,   label: "Helado",           category: "Nutrición", tags: ["helado","postre","dulce","cheat"] },
  { id: "pizza",          Component: Pizza,          label: "Pizza",            category: "Nutrición", tags: ["pizza","comida","cheat","carbo"] },
  { id: "salad",          Component: Salad,          label: "Ensalada",         category: "Nutrición", tags: ["ensalada","verduras","saludable","verde"] },
  { id: "sandwich",       Component: Sandwich,       label: "Sandwich",         category: "Nutrición", tags: ["sandwich","comida","almuerzo"] },
  { id: "soup",           Component: Soup,           label: "Sopa",             category: "Nutrición", tags: ["sopa","caliente","cena"] },
  { id: "wheat",          Component: Wheat,          label: "Trigo / Cereal",   category: "Nutrición", tags: ["trigo","carbo","cereal","grano"] },
  { id: "glass-water",    Component: GlassWater,     label: "Agua",             category: "Nutrición", tags: ["agua","hidratación","vaso"] },
  { id: "milk",           Component: Milk,           label: "Leche",            category: "Nutrición", tags: ["leche","lácteo","calcio"] },

  // === Métricas ===
  { id: "scale",          Component: Scale,          label: "Balanza",          category: "Métricas",  tags: ["balanza","peso","kg","báscula"] },
  { id: "ruler",          Component: Ruler,          label: "Regla",            category: "Métricas",  tags: ["regla","medida","cm"] },
  { id: "gauge",          Component: Gauge,          label: "Velocímetro",      category: "Métricas",  tags: ["velocidad","intensidad","gauge"] },
  { id: "trending-up",    Component: TrendingUp,     label: "Tendencia ↑",      category: "Métricas",  tags: ["progreso","mejorando","subir","ganar"] },
  { id: "trending-down",  Component: TrendingDown,   label: "Tendencia ↓",      category: "Métricas",  tags: ["bajar","perder","grasa","déficit"] },
  { id: "chart-bar",      Component: ChartBar,       label: "Barras",           category: "Métricas",  tags: ["barras","gráfico","stats"] },
  { id: "chart-line",     Component: ChartLine,      label: "Línea",            category: "Métricas",  tags: ["línea","gráfico","tendencia"] },
  { id: "percent",        Component: Percent,        label: "Porcentaje",       category: "Métricas",  tags: ["porcentaje","%","grasa","masa"] },
  { id: "hash",           Component: Hash,           label: "Numeral",          category: "Métricas",  tags: ["número","hashtag","#","reps"] },

  // === Logros ===
  { id: "trophy",         Component: Trophy,         label: "Trofeo",           category: "Logros",    tags: ["trofeo","ganador","logro","premio"] },
  { id: "medal",          Component: Medal,          label: "Medalla",          category: "Logros",    tags: ["medalla","podio","logro"] },
  { id: "award",          Component: Award,          label: "Premio",           category: "Logros",    tags: ["premio","reconocimiento","cinta"] },
  { id: "star",           Component: Star,           label: "Estrella",         category: "Logros",    tags: ["estrella","destacado","favorito"] },
  { id: "crown",          Component: Crown,          label: "Corona",           category: "Logros",    tags: ["corona","rey","premium","élite"] },
  { id: "target",         Component: Target,         label: "Diana",            category: "Logros",    tags: ["objetivo","meta","target","goal"] },
  { id: "sparkles",       Component: Sparkles,       label: "Brillo",           category: "Logros",    tags: ["brillo","destellos","premium","calidad"] },

  // === Naturaleza ===
  { id: "sun",            Component: Sun,            label: "Sol",              category: "Naturaleza", tags: ["sol","día","mañana","luz"] },
  { id: "sunrise",        Component: Sunrise,        label: "Amanecer",         category: "Naturaleza", tags: ["amanecer","mañana","temprano"] },
  { id: "sunset",         Component: Sunset,         label: "Atardecer",        category: "Naturaleza", tags: ["atardecer","tarde","puesta"] },
  { id: "moon",           Component: Moon,           label: "Luna",             category: "Naturaleza", tags: ["luna","noche","descanso","sueño"] },
  { id: "snowflake",      Component: Snowflake,      label: "Copo de nieve",    category: "Naturaleza", tags: ["nieve","invierno","frío"] },
  { id: "cloud-sun",      Component: CloudSun,       label: "Sol con nube",     category: "Naturaleza", tags: ["clima","nublado","sol"] },
  { id: "leaf",           Component: Leaf,           label: "Hoja",             category: "Naturaleza", tags: ["hoja","natural","verde","planta"] },
  { id: "sprout",         Component: Sprout,         label: "Brote",            category: "Naturaleza", tags: ["brote","crecimiento","planta"] },
  { id: "tree-pine",      Component: TreePine,       label: "Pino",             category: "Naturaleza", tags: ["árbol","pino","outdoor"] },
  { id: "waves",          Component: Waves,          label: "Olas",             category: "Naturaleza", tags: ["agua","olas","mar","natación"] },

  // === Bienestar ===
  { id: "smile",          Component: Smile,          label: "Sonrisa",          category: "Bienestar", tags: ["sonrisa","feliz","positivo"] },
  { id: "meh",            Component: Meh,            label: "Neutral",          category: "Bienestar", tags: ["neutral","cara","emoción"] },
  { id: "thumbs-up",      Component: ThumbsUp,       label: "Pulgar arriba",    category: "Bienestar", tags: ["ok","bien","aprobado","like"] },
  { id: "music",          Component: Music,          label: "Música",           category: "Bienestar", tags: ["música","playlist","entreno"] },
  { id: "headphones",     Component: Headphones,     label: "Auriculares",      category: "Bienestar", tags: ["auriculares","música","cardio"] },

  // === UI ===
  { id: "check",          Component: Check,          label: "Check",            category: "UI / Acciones", tags: ["check","hecho","ok"] },
  { id: "check-check",    Component: CheckCheck,     label: "Doble check",      category: "UI / Acciones", tags: ["check","completo","doble","verificado"] },
  { id: "x",              Component: X,              label: "Cerrar / No",      category: "UI / Acciones", tags: ["x","cerrar","no","cancelar"] },
  { id: "plus",           Component: Plus,           label: "Más",              category: "UI / Acciones", tags: ["más","plus","agregar"] },
  { id: "minus",          Component: Minus,          label: "Menos",            category: "UI / Acciones", tags: ["menos","quitar","restar"] },
  { id: "chevron-right",  Component: ChevronRight,   label: "Flecha derecha",   category: "UI / Acciones", tags: ["flecha","derecha","siguiente"] },
  { id: "chevron-left",   Component: ChevronLeft,    label: "Flecha izquierda", category: "UI / Acciones", tags: ["flecha","izquierda","atrás"] },
  { id: "chevron-up",     Component: ChevronUp,      label: "Flecha arriba",    category: "UI / Acciones", tags: ["flecha","arriba","subir"] },
  { id: "chevron-down",   Component: ChevronDown,    label: "Flecha abajo",     category: "UI / Acciones", tags: ["flecha","abajo","bajar"] },
  { id: "arrow-up",       Component: ArrowUp,        label: "Subir",            category: "UI / Acciones", tags: ["arriba","subir","arrow"] },
  { id: "arrow-down",     Component: ArrowDown,      label: "Bajar",            category: "UI / Acciones", tags: ["abajo","bajar","arrow"] },
  { id: "arrow-right",    Component: ArrowRight,     label: "Derecha",          category: "UI / Acciones", tags: ["derecha","siguiente","arrow"] },
  { id: "play",           Component: Play,           label: "Play",             category: "UI / Acciones", tags: ["play","iniciar"] },
  { id: "pause",          Component: Pause,          label: "Pausa",            category: "UI / Acciones", tags: ["pausa","stop","descanso"] },
  { id: "circle-play",    Component: CirclePlay,     label: "Play círculo",     category: "UI / Acciones", tags: ["play","empezar","botón"] },

  // === Formas ===
  { id: "circle-dot",     Component: CircleDot,      label: "Círculo punto",    category: "Formas",    tags: ["círculo","punto","forma"] },
  { id: "circle",         Component: Circle,         label: "Círculo",          category: "Formas",    tags: ["círculo","forma","redondo"] },
  { id: "square",         Component: Square,         label: "Cuadrado",         category: "Formas",    tags: ["cuadrado","forma"] },
  { id: "triangle",       Component: Triangle,       label: "Triángulo",        category: "Formas",    tags: ["triángulo","forma"] },
  { id: "hexagon",        Component: Hexagon,        label: "Hexágono",         category: "Formas",    tags: ["hexágono","forma"] },

  // === Salud ===
  { id: "stethoscope",    Component: Stethoscope,    label: "Estetoscopio",     category: "Salud",     tags: ["estetoscopio","médico","salud","doctor"] },
  { id: "pill",           Component: Pill,           label: "Pastilla",         category: "Salud",     tags: ["pastilla","medicamento","suplemento"] },
  { id: "bandage",        Component: Bandage,        label: "Curita",           category: "Salud",     tags: ["curita","lesión","herida"] },
  { id: "syringe",        Component: Syringe,        label: "Jeringa",          category: "Salud",     tags: ["jeringa","médico","vacuna"] },
  { id: "thermometer",    Component: Thermometer,    label: "Termómetro",       category: "Salud",     tags: ["termómetro","temperatura","fiebre"] },

  // === Equipo gym ===
  { id: "anchor",         Component: Anchor,         label: "Ancla",            category: "Equipo gym", tags: ["ancla","fijo","peso"] },
  { id: "cable",          Component: Cable,          label: "Polea",            category: "Equipo gym", tags: ["polea","cable","máquina"] },
  { id: "link",           Component: LinkIcon,       label: "Cadena",           category: "Equipo gym", tags: ["cadena","conexión","link"] },

  // === Otros ===
  { id: "bookmark",       Component: Bookmark,       label: "Marcador",         category: "Otros",     tags: ["marcador","guardar","favorito"] },
  { id: "book-open",      Component: BookOpen,       label: "Libro abierto",    category: "Otros",     tags: ["libro","leer","conocimiento"] },
  { id: "camera",         Component: Camera,         label: "Cámara",           category: "Otros",     tags: ["cámara","foto","registro"] },
  { id: "compass",        Component: Compass,        label: "Brújula",          category: "Otros",     tags: ["brújula","dirección","norte"] },
  { id: "flag",           Component: Flag,           label: "Bandera",          category: "Otros",     tags: ["bandera","meta","objetivo"] },
  { id: "lightbulb",      Component: Lightbulb,      label: "Bombilla",         category: "Otros",     tags: ["idea","luz","tip","consejo"] },
  { id: "map-pin",        Component: MapPin,         label: "Pin de mapa",      category: "Otros",     tags: ["ubicación","mapa","lugar"] },
  { id: "quote",          Component: Quote,          label: "Comillas",         category: "Otros",     tags: ["cita","frase","comillas"] },
  { id: "rocket",         Component: Rocket,         label: "Cohete",           category: "Otros",     tags: ["cohete","despegar","launch","rápido"] },
  { id: "shield",         Component: Shield,         label: "Escudo",           category: "Otros",     tags: ["escudo","proteger","defensa"] },
  { id: "sword",          Component: Sword,          label: "Espada",           category: "Otros",     tags: ["espada","guerrero","ataque"] },
  { id: "tag",            Component: Tag,            label: "Etiqueta",         category: "Otros",     tags: ["etiqueta","tag","label"] },
];

/** Mapa rápido por id para render con `<LibraryIcon id="..." />`. */
const ICON_MAP: Map<string, IconEntry> = new Map(ICON_CATALOG.map((e) => [e.id, e]));

/** Lookup por id. Devuelve undefined si el id no existe (ej. plan viejo con
 *  un id obsoleto). El consumidor debe manejar el caso. */
export function getIconEntry(id: string | null | undefined): IconEntry | undefined {
  if (!id) return undefined;
  return ICON_MAP.get(id);
}

/**
 * Render component por id con tamaño + color configurable. Útil para
 * renderizar decoraciones del plan donde solo guardamos el id en JSONB.
 *
 * Soporta DOS librerías:
 *   - Lucide (legacy): `id` sin prefijo, ej. `"dumbbell"` → ICON_CATALOG
 *   - Phosphor:        `id` con prefijo `"ph:<name>:<weight>"`, ej.
 *                      `"ph:barbell:fill"` → PHOSPHOR_CATALOG con weight
 *
 * Si el id no matchea con ningún catálogo, devuelve null (render gracioso
 * para planes legacy con ids obsoletos).
 */
export interface LibraryIconProps {
  id: string;
  size?: number;
  color?: string;
  strokeWidth?: number;
}

export function LibraryIcon({ id, size = 24, color = "currentColor", strokeWidth = 2 }: LibraryIconProps) {
  // Phosphor: `ph:<icon-id>:<weight>` (weight default "regular" si falta)
  if (id.startsWith("ph:")) {
    const parts = id.split(":");
    const phId = parts[1] || "";
    const weight = (parts[2] || "regular") as "light" | "regular" | "bold" | "fill";
    const phEntry = getPhosphorEntry(phId);
    if (!phEntry) return null;
    const C = phEntry.Component;
    return <C size={size} color={color} weight={weight} aria-hidden="true" />;
  }
  // Nilabs asset: `asset:<id>` — render como <img> ya que es SVG/PNG arbitrario.
  // El color no se aplica (es un asset visual completo, no un glyph). Tamaño
  // controlado por el contenedor padre vía size; usamos object-fit:contain.
  if (id.startsWith("asset:")) {
    const assetUrl = useNilabsAssetUrl(id.slice("asset:".length));
    if (!assetUrl) return null;
    return (
      <img
        src={assetUrl}
        alt=""
        aria-hidden="true"
        style={{
          width: size,
          height: size,
          objectFit: "contain",
          display: "block",
        }}
      />
    );
  }
  // Lucide (legacy / default)
  const entry = getIconEntry(id);
  if (!entry) return null;
  const C = entry.Component;
  return <C size={size} color={color} strokeWidth={strokeWidth} aria-hidden="true" />;
}

/**
 * Cache global de assets Nilabs (id → url). Carga una sola vez al primer uso
 * y persiste durante la sesión del browser. Evita N fetches cuando hay
 * múltiples decoraciones del mismo asset en distintas hojas del plan.
 *
 * No persiste a localStorage porque los URLs podrían cambiar si el admin
 * re-sube un asset; la sesión efímera está bien.
 */
const _assetCache: { map: Map<string, string> | null; loading: Promise<void> | null } = {
  map: null,
  loading: null,
};

function useNilabsAssetUrl(assetId: string): string | null {
  // No usamos useState para no re-renderizar cada decoración cuando carga
  // un asset distinto; en su lugar usamos un ref ligero al cache global y
  // un useEffect que dispara render local solo cuando el id objetivo aparece.
  const [, force] = React.useState(0);
  React.useEffect(() => {
    if (_assetCache.map) return; // ya cargado
    if (_assetCache.loading) {
      _assetCache.loading.then(() => force((v) => v + 1));
      return;
    }
    // Lazy import para no bundlear api/admin si nadie usa assets
    _assetCache.loading = import("../api/admin")
      .then((m) => m.listAssetsForCoach())
      .then((list) => {
        _assetCache.map = new Map(list.map((a) => [String(a.id), a.url]));
        _assetCache.loading = null;
        force((v) => v + 1);
      })
      .catch(() => {
        _assetCache.map = new Map();
        _assetCache.loading = null;
      });
  }, []);
  return _assetCache.map?.get(assetId) ?? null;
}

/** Categorías en el orden que aparecen en el picker. */
export const ICON_CATEGORIES: IconCategory[] = [
  "Ejercicio",
  "Anatomía",
  "Tiempo",
  "Nutrición",
  "Métricas",
  "Logros",
  "Naturaleza",
  "Bienestar",
  "UI / Acciones",
  "Formas",
  "Salud",
  "Equipo gym",
  "Otros",
];

/** Búsqueda con tokens + tags para que matchee tanto label como sinónimos
 *  ("kg" matchea Balanza por tag "kg"). */
export function searchIcons(query: string): IconEntry[] {
  const q = (query || "").trim().toLowerCase();
  if (!q) return ICON_CATALOG;
  const tokens = q.split(/\s+/).filter(Boolean);
  return ICON_CATALOG.filter((e) => {
    const haystack = `${e.label.toLowerCase()} ${e.tags.join(" ")} ${e.id}`;
    return tokens.every((t) => haystack.includes(t));
  });
}
